import { CommonModule } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldType } from '@ngx-formly/core';
import { IntegrationStatus } from '../../../generated/generated-types';

@Component({
    selector: 'app-formly-field-status-badge',
    templateUrl: './formly-field-status-badge.component.html',
    styleUrl: './formly-field-status-badge.component.scss',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule
    ]
})
export class FormlyFieldStatusBadgeComponent extends FieldType {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    super();

    afterNextRender({ mixedReadWrite: () => {
        // valueChanges must be torn down; re-opening integration status fields stacked listeners otherwise.
        this.form.get('status')?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                this.getStatusClass(value);
                this.cdr.markForCheck();
            });
    } }, );
  }

  getStatusClass(status: IntegrationStatus) {
    switch (status) {
      case IntegrationStatus.CONNECTED: return 'status-connected';
      case IntegrationStatus.DISCONNECTED: return 'status-disconnected';
      case IntegrationStatus.PENDING: return 'status-pending';
      case IntegrationStatus.UNKNOWN: return 'status-unknown';
      case IntegrationStatus.UNAUTHORIZED: return 'status-unauthorized';
      case IntegrationStatus.FORBIDDEN: return 'status-forbidden';
      case IntegrationStatus.NOT_FOUND: return 'status-not-found';
      case IntegrationStatus.INTERNAL_SERVER_ERROR: return 'status-internal-server-error';
      case IntegrationStatus.TOO_MANY_REQUESTS: return 'status-too-many-requests';
      case IntegrationStatus.ECONNREFUSED: return 'status-econnrefused';
      default: {
        this.formControl.setValue(IntegrationStatus.UNKNOWN);
        return 'status-unknown';
      }
    }
  }

  getStatusText(status: IntegrationStatus) {
    switch (status) {
      case IntegrationStatus.CONNECTED: return 'Connected';
      case IntegrationStatus.DISCONNECTED: return 'Disconnected';
      case IntegrationStatus.PENDING: return 'Pending';
      case IntegrationStatus.UNKNOWN: return 'Unknown';
      case IntegrationStatus.UNAUTHORIZED: return 'Unauthorized';
      case IntegrationStatus.FORBIDDEN: return 'Forbidden';
      case IntegrationStatus.NOT_FOUND: return 'Not Found';
      case IntegrationStatus.INTERNAL_SERVER_ERROR: return 'Internal Server Error';
      case IntegrationStatus.TOO_MANY_REQUESTS: return 'Too Many Requests';
      case IntegrationStatus.ECONNREFUSED: return 'Connection Refused';
      default: {
        this.formControl.setValue(IntegrationStatus.UNKNOWN);
        return 'Unknown';
      }
    }
  }
}
